
import Image from 'next/image';
import Link from 'next/link';
import SvgRenderer from '../Common/SvgRenderer';
import {
  ChangeLogRecord,
  FooterQuery,
  LegalPageRecord,
  SiteLocale,
} from '@/graphql/types/graphql';
import { notFound } from 'next/navigation';
import { primaryColor } from '@/app/i18n/settings';
import ReactMarkdown from 'react-markdown';

type Props = {
  data: FooterQuery;
  lng: SiteLocale;
};

const Footer = ({ data, lng }: Props) => {
  return (
    <footer className="relative z-10 mx-auto flex w-full flex-col items-center justify-center bg-primary bg-opacity-5 pt-16 text-center md:text-start lg:pt-24">
      <div className="container w-full">
        <div className="flex w-full flex-col justify-between md:flex-row md:px-16">
          <div className="w-full">
            <div className="mx- mb-12 lg:mb-16">
              <Link href={'/' + lng + '/home'} className="mb-8 inline-block">
                {data.layout?.footerLogo && (
                  <Image
                    src={(data.layout.footerLogo.responsiveImage as any)?.src || '/images/placeholder-logo.png'}
                    alt="logo"
                    className="w-full"
                    width={(data.layout.footerLogo.responsiveImage as any)?.width || 100}
                    height={(data.layout.footerLogo.responsiveImage as any)?.height || 100}
                  />
                )}
              </Link>
              <div className="mb-9 text-base font-medium leading-relaxed text-body-color">
                <ReactMarkdown>
                  {data.layout!.footerSubtitle || ''}
                </ReactMarkdown>
              </div>
              <div className="flex items-center justify-center md:justify-start">
                {data.layout!.socialMediaLinks.map((socialMedia) => {
                  // Determine fallback icon based on URL or name
                  let fallbackIcon = '/images/placeholder-icon.svg';
                  if (socialMedia.url) {
                    if (socialMedia.url.includes('facebook.com')) fallbackIcon = '/images/facebook.svg';
                    else if (socialMedia.url.includes('twitter.com') || socialMedia.url.includes('x.com')) fallbackIcon = '/images/x.svg';
                    else if (socialMedia.url.includes('youtube.com')) fallbackIcon = '/images/youtube.svg';
                    else if (socialMedia.url.includes('linkedin.com')) fallbackIcon = '/images/linkedin.svg';
                  }
                  return (
                    <a
                      href={socialMedia.url}
                      aria-label="social-link"
                      className="mr-6 text-[#CED3F6] hover:text-primary"
                      key={socialMedia.id}
                    >
                      <SvgRenderer url={(socialMedia.icon?.responsiveImage as any)?.src || fallbackIcon} />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex w-full md:text-end">
            <div className="w-full">
              <div className="mb-12 lg:mb-16">
                <h2 className="mb-10 text-xl font-bold text-black dark:text-white">
                  Legal
                </h2>
                <ul>
                  {data.layout!.footerLinks.map((link) => {
                    const pageLink = link as LegalPageRecord; // The field has a "at least one" validation
                    return (
                      <li key={pageLink.id}>
                        <a
                          href={'/' + lng + '/legal/' + pageLink.slug}
                          className="mb-4 inline-block text-base font-medium text-body-color hover:text-primary"
                        >
                          {' '}
                          {pageLink.title}{' '}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SVG and other content above this line */}
      <div className="absolute right-0 top-14 z-[-1]">
        <svg
          width="55"
          height="99"
          viewBox="0 0 55 99"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* SVG content */}
        </svg>
      </div>

      <div className="absolute bottom-24 left-0 z-[-1]">
        <svg
          width="79"
          height="94"
          viewBox="0 0 79 94"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* SVG content */}
        </svg>
      </div>

      {/* Credit section positioned at the bottom-left */}
      <div
  style={{
    position: 'absolute',
    bottom: '10px',
    left: '10px',
  }}
  dangerouslySetInnerHTML={{
    __html: ``,
  }}
/>
    </footer>
  );
};

export default Footer;
